import React, { useEffect, useState } from "react";
import { ColorDropdown } from "@pages/register/_components";
import PatternDropdown from "./_components/PatternDropdown";
import TagAttributeSelector from "./_components/TagAttributeSelector";
import GenderSelector from "./_components/GenderSelector";
import TypeCategorySelector from "./_components/TypeCategorySelector";
import LabelWithInput from "@components/input/LabelWithInput";
import BasicButton from "@components/button/BasicButton";
import styled from "@emotion/styled";
import BackHeader from "@components/header/BackHeader";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import { Flex } from "antd";
import useModalStore from "@store/modalStore";
import BasicModal from "@components/modal/BasicModal";
import { ModalContent } from "@components/modal/ModalContent";
import { useNavigate } from "react-router-dom";
import useUserInfoStore from "@store/useUserInfoStore";
import ToastAlert from "@components/alert/ToastAlert";
import { FormProvider, useForm } from "react-hook-form";
import { aiProductSchema } from "@libs/zod/zodVaildation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useImageAnalysisStore } from "@store/imageAnalysis/useImageAnalysisStore";
import { useApiRequest } from "@utils/ApiRequest";
import { useMutation, useQuery } from "@tanstack/react-query";
import useSelectedColorStore from "@store/register/useSelectedColor";
import {
  analyzeLLMTags,
  LLMTagResult,
} from "@utils/llmTagApi";

const normalizeTypeKey = (value?: string | null) => {
  if (!value) return "";

  const normalized = value.toLowerCase().replace(/[^a-z]/g, "");
  const aliasMap: Record<string, string> = {
    tshirt: "tshirts",
    tshirts: "tshirts",
    shirts: "shirts",
    shirt: "shirts",
    skirts: "skirts",
    skirt: "skirts",
    dresses: "dress",
    dress: "dress",
  };

  return aliasMap[normalized] || normalized;
};

const CaptureDetail = () => {
  const [selectedColor, setSelectedColor] = useState<string>("선택");
  const [openColorDropdown, setOpenColorDropdown] = useState(false);
  const [llmResult, setLlmResult] = useState<LLMTagResult | null>(null);
  const [llmLoading, setLlmLoading] = useState(false);
  const [openPatternDropdown, setOpenPatternDropdown] = useState(false);
  const [submitType, setSubmitType] = useState<"stock" | "product" | null>(
    null,
  );
  const navigate = useNavigate();
  const theme = useTheme();
  const { postAiTrainingData, getColorList, getColorId, getTagList } =
    useApiRequest();

  const { modals, openModal, closeModal } = useModalStore();
  const { userInfo } = useUserInfoStore();
  const { aiAnalysisResult, uploadedImageUrl, analysisCompletedAt, suggestedColors, clearData } =
    useImageAnalysisStore();
  const { colorId, setColorId, setColor } = useSelectedColorStore();

  // ** 1. status
  const isAdmin = userInfo?.role === "ADMIN";

  // 정책 설정확인
  const isSalesPolicy =
    isAdmin || userInfo?.shipping_fee !== null || userInfo?.free_shipping_fee !== null;

  // 계좌 등록 확인
  const isAccount = isAdmin || userInfo?.account_number !== null;

  // ** 2. query
  const AiColor = aiAnalysisResult?.color_classification?.id;

  const { data: colorList } = useQuery({
    queryKey: ["colorList"],
    queryFn: getColorList,
  });

  const { data: AiColorId } = useQuery({
    queryKey: ["colorId", AiColor],
    queryFn: () => getColorId(String(AiColor)),
    enabled:
      AiColor != null && !aiAnalysisResult?.color_classification?.is_multi,
  });

  const { data: typeTagListRaw } = useQuery({
    queryKey: ["tagList", "type"],
    queryFn: () => getTagList("type"),
  });

  // 태그 목록 DB 조회
  const { data: styleTagListRaw } = useQuery({
    queryKey: ["tagList", "style"],
    queryFn: () => getTagList("style"),
  });
  const { data: fitTagListRaw } = useQuery({
    queryKey: ["tagList", "silhouette"],
    queryFn: () => getTagList("silhouette"),
  });
  const { data: occasionTagListRaw } = useQuery({
    queryKey: ["tagList", "curation"],
    queryFn: () => getTagList("curation"),
  });
  const { data: patternTagListRaw } = useQuery({
    queryKey: ["tagList", "pattern"],
    queryFn: () => getTagList("pattern"),
  });

  const PatternOptions = (patternTagListRaw || [])
    .filter((tag: any) => tag.is_active)
    .map((tag: any) => ({
      id: Number(tag.id),
      name: `${tag.name_ko} / ${tag.name_en}`,
      nameKo: tag.name_ko,
      nameEn: tag.name_en,
    }));

  // 백엔드 응답(snake_case) → 기존 형식 변환 (LLM은 영문 태그명 반환 → name_en 사용)
  const TypeTagList: { id: number; name: string }[] = (typeTagListRaw || [])
    .filter((t: any) => t.is_active)
    .map((t: any) => ({ id: t.id, name: t.name_en }));

  const StyleTagList: { id: number; name: string }[] = (styleTagListRaw || [])
    .filter((t: any) => t.is_active)
    .map((t: any) => ({ id: t.id, name: t.name_en }));

  const FitTagList: { id: number; name: string }[] = (fitTagListRaw || [])
    .filter((t: any) => t.is_active)
    .map((t: any) => ({ id: t.id, name: t.name_en }));

  const OccasionTagList: { id: number; name: string }[] = (occasionTagListRaw || [])
    .filter((t: any) => t.is_active)
    .map((t: any) => ({ id: t.id, name: t.name_en }));

  // 재고등록
  const { mutate } = useMutation({
    mutationFn: (data: any) => postAiTrainingData(data),
    onSuccess: (res) => {
      console.log("[API] mutate onSuccess 전체 응답:", JSON.stringify(res));
      const id = res?.id;
      if (!id) {
        console.error("[API] 등록 응답에 id 없음:", res, "typeof:", typeof res);
        ToastAlert("등록 처리 중 문제가 발생했습니다.");
        return;
      }
      if (submitType === "stock") {
        navigate(`/stock-register/${id}`, { replace: true });
      } else if (submitType === "product") {
        navigate(`/register/${id}`, {
          replace: true,
          state: { origin: "capture", returnTo: "/capture-detail" },
        });
      }
      clearData();
    },
    onError: (error: any) => {
      console.error("[API] 등록 실패:", error);
      ToastAlert("등록에 실패했습니다. 다시 시도해주세요.");
    },
  });

  // ** 3. React Hook Form
  const methods = useForm({
    resolver: zodResolver(aiProductSchema()),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      image: null,
      category: null,
      depth1: null,
      depth2: null,
      depth1_size_type_id: null,
      depth2_size_type_id: null,
      size_type_id: null,
      color: {
        color_id: AiColorId?.color_id || colorId,
      },
      pattern: {
        pattern_id: -99,
        pattern_name: "선택",
      },
      // 태그 속성 필드
      tag_type: [] as number[],
      tag_style: [] as number[],
      tag_fit: [] as number[],
      tag_occasion: [] as number[],
      // 성별
      gender: "" as string,
      audience_category_ids: [] as number[],
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = methods;

  // LLM 결과 → 태그 목록 변환 헬퍼
  const matchTags = (
    llmNames: string[] | undefined,
    tagList: { id: number; name: string }[]
  ) => {
    if (!llmNames || llmNames.length === 0) return [];
    const matched = llmNames
      .map((name) =>
        tagList.find((t) => t.name.toLowerCase() === name.toLowerCase()),
      )
      .filter(Boolean) as { id: number; name: string }[];

    return matched.filter(
      (tag, index, array) => array.findIndex((item) => item.id === tag.id) === index,
    ).slice(0, 2);
  };

  const typeOptions = (aiAnalysisResult?.clothing_classification.predictions || [])
    .slice(0, 3)
    .map((prediction) => {
      const matchedTag = TypeTagList.find(
        (tag) => normalizeTypeKey(tag.name) === normalizeTypeKey(prediction.class_name),
      );
      if (!matchedTag) return null;

      return {
        tag_id: matchedTag.id,
        label: matchedTag.name,
        rank: prediction.rank,
      };
    })
    .filter(Boolean) as { tag_id: number; label: string; rank?: number }[];

  const aiStyleTags = matchTags(llmResult?.style, StyleTagList);
  const aiFitTags = matchTags(llmResult?.fit, FitTagList);
  const aiOccasionTags = matchTags(llmResult?.occasion, OccasionTagList);

  const onSubmit = async (data: any, type: "stock" | "product") => {
    const colorIdData = data.color ? data.color.color_id : AiColorId?.color_id;
    const selectedCategoryId = data.audience_category_ids?.[0] || null;

    const postData = {
      category_id: selectedCategoryId,
      color_id: colorIdData,
      pattern: data.pattern?.pattern_name,
      classification: {
        audience_category_ids: data.audience_category_ids || [],
        patterns: [data.pattern?.pattern_id ? { tag_id: data.pattern.pattern_id } : { name_ko: data.pattern?.name_ko, name_en: data.pattern?.name_en, name_ja: data.pattern?.name_ja }],
      },
      color_input: { ...data.color, color_hex_code: data.color?.hex_code, size_list: [] },
      ai_image_path: aiAnalysisResult?.image_path,
      image_url: uploadedImageUrl,
      // 태그 속성 데이터
      tag_type: data.tag_type || [],
      tag_style: data.tag_style || [],
      tag_fit: data.tag_fit || [],
      tag_occasion: data.tag_occasion || [],
      // 성별
      gender: data.gender || null,
    };

    console.log("[API] POST /api/ai-training-data", postData);

    if (type === "stock") {
      setSubmitType("stock");
      mutate(postData);
    } else if (type === "product") {
      if (!isSalesPolicy) {
        openModal("policyModal");
        return;
      }
      if (!isAccount) {
        openModal("bankAccountModal");
        return;
      }
      setSubmitType("product");
      mutate(postData);
    }
  };

  // ** 4.useEffect
  // AI 색상 값 선택
  useEffect(() => {
    if (!colorList) return;

    const targetColorId = suggestedColors?.[0]?.color_id ??
      (aiAnalysisResult?.color_classification?.is_multi ? 14 : AiColorId?.color_id);

    if (targetColorId == null) return;

    const matchedColor = colorList.find((c: any) => c.id === targetColorId);

    if (matchedColor) {
      setSelectedColor(matchedColor.name);
      setColorId(matchedColor.id);
      setColor(matchedColor.name);
      setValue("color.color_id", matchedColor.id);
    } else {
      console.warn(
        `[색상 매핑] color_id=${targetColorId}에 해당하는 색상을 찾을 수 없습니다. AI color_id: ${AiColor}`
      );
    }
  }, [
    colorList,
    AiColor,
    AiColorId?.color_id,
    suggestedColors,
    aiAnalysisResult?.color_classification.is_multi,
    setSelectedColor,
    setColorId,
    setColor,
    setValue,
  ]);

  // AI 분류 결과에 맞는 패턴 찾기
  useEffect(() => {
    const aiClassName =
      aiAnalysisResult?.pattern_classification?.predictions[0]?.class_name;

    if (aiClassName) {
      const match = PatternOptions.find(
        (p: any) => p.nameEn.toLowerCase() === aiClassName.toLowerCase()
      );
      const current = getValues("pattern");

      const isDefault = !current || current.pattern_id === -99;

      if (match && isDefault) {
        setValue("pattern", {
          pattern_id: match.id,
          pattern_name: match.name,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiAnalysisResult]);

  // LLM 태그 분석 호출 (이미지 URL이 있으면 자동 실행)
  useEffect(() => {
    if (!uploadedImageUrl || llmResult) return;

    const runLLM = async () => {
      setLlmLoading(true);
      try {
        console.log("[LLM] 태그 분석 시작:", uploadedImageUrl);
        const result = await analyzeLLMTags(null, uploadedImageUrl);
        console.log("[LLM] 태그 분석 완료:", result);
        setLlmResult(result);
      } catch (err) {
        console.error("[LLM] 태그 분석 실패:", err);
      } finally {
        setLlmLoading(false);
      }
    };

    runLLM();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedImageUrl]);

  // LLM 추천 태그 자동 선택 (스타일, 실루엣/핏, 큐레이션)
  useEffect(() => {
    if (!llmResult) return;

    if (aiStyleTags.length > 0 && (getValues("tag_style") || []).length === 0) {
      setValue("tag_style", aiStyleTags.map((t) => t.id), { shouldValidate: true });
    }
    if (aiFitTags.length > 0 && (getValues("tag_fit") || []).length === 0) {
      setValue("tag_fit", aiFitTags.map((t) => t.id), { shouldValidate: true });
    }
    if (aiOccasionTags.length > 0 && (getValues("tag_occasion") || []).length === 0) {
      setValue("tag_occasion", aiOccasionTags.map((t) => t.id), { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llmResult, styleTagListRaw, fitTagListRaw, occasionTagListRaw]);

  // 분류 모델 타입 태그 자동 선택
  useEffect(() => {
    if (typeOptions.length > 0 && (getValues("tag_type") || []).length === 0) {
      setValue("tag_type", [typeOptions[0].tag_id], { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiAnalysisResult, typeTagListRaw]);

  // ** 5. function
  const formatKoreanDateTime = (isoString: any) => {
    const date = new Date(isoString);
    const yyyy = date.getFullYear();
    const mm = date.getMonth() + 1;
    const dd = date.getDate();
    const hh = date.getHours().toString().padStart(2, "0");
    const min = date.getMinutes().toString().padStart(2, "0");
    const ss = date.getSeconds().toString().padStart(2, "0");
    return `${yyyy}. ${mm}. ${dd} ${hh}:${min}:${ss}`;
  };

  const handleBackEvent = () => {
    openModal("backModal");
  };

  return (
    <FormProvider {...methods}>
      <BackHeader title="" rightElement onClick={handleBackEvent} />

      <Image
        src={uploadedImageUrl ? uploadedImageUrl : ""}
        width="100%"
        height="37.5rem"
        minWidth="37.5rem"
      />

      <Container>
        <StyledDate>{formatKoreanDateTime(analysisCompletedAt)}</StyledDate>

        <NoticeBox>
          <Image
            src={theme.image.warning_circle_green.default}
            height="1.6rem"
            width="1.6rem"
          />

          <div>
            <StrongText>
              {llmLoading
                ? "AI가 상품을 분석하고 있습니다..."
                : "AI가 자동으로 태그를 선택했습니다."}
            </StrongText>
            <NormalText>
              {llmLoading
                ? "잠시만 기다려주세요."
                : (
                  <>
                    변경이 필요하다면 다른 태그를 선택하거나, <br />
                    직접 선택을 통해 변경해주세요.
                  </>
                )}
            </NormalText>
          </div>
        </NoticeBox>

        <div style={{ paddingTop: "1rem" }} />

        {/* 속성 - 타입 + 카테고리 통합 */}
        <TypeCategorySelector
          typeOptions={typeOptions}
          allTypeTags={TypeTagList}
        />

        {/* 속성 - 스타일 */}
        <TagAttributeSelector
          label="속성 - 스타일"
          fieldName="tag_style"
          aiTags={aiStyleTags}
          allTags={StyleTagList}
          required
          loading={llmLoading}
          maxSelection={2}
        />

        {/* 속성 - 실루엣/핏 */}
        <TagAttributeSelector
          label="속성 - 실루엣/핏"
          fieldName="tag_fit"
          aiTags={aiFitTags}
          allTags={FitTagList}
          required
          loading={llmLoading}
          maxSelection={2}
        />

        {/* 속성 - 큐레이션 */}
        <TagAttributeSelector
          label="속성 - 큐레이션"
          fieldName="tag_occasion"
          aiTags={aiOccasionTags}
          allTags={OccasionTagList}
          required
          loading={llmLoading}
          maxSelection={2}
        />

        {/* 색상 드롭다운 */}
        <LabelWithInput label={"색상"} required />
        <ColorDropdown
          name="color"
          open={openColorDropdown}
          setOpen={setOpenColorDropdown}
          selectedValue={selectedColor}
          setSelectedValue={setSelectedColor}
          mode="single"
        />

        {errors.color?.color_id && (
          <ErrorMessage>{String(errors.color.color_id.message)}</ErrorMessage>
        )}

        {/* 패턴 드롭다운 */}
        <LabelWithInput label={"패턴"} required />
        <PatternDropdown
          name="pattern"
          options={PatternOptions}
          open={openPatternDropdown}
          setOpen={setOpenPatternDropdown}
        />

        {errors.pattern && (
          <ErrorMessage>{errors.pattern?.pattern_name?.message}</ErrorMessage>
        )}

        {/* 성별 */}
        <GenderSelector />

        {errors.gender && (
          <ErrorMessage>{String((errors.gender as any).message)}</ErrorMessage>
        )}

        <ButtonWrapper>
          <BasicButton
            title="재고등록"
            transparent
            borderColor="#111"
            padding="1.6rem"
            fontSize="1.6rem"
            height="5.2rem"
            width="100%"
            disabled={llmLoading}
            onClick={() => {
              handleSubmit(
                (data) => onSubmit(data, "stock"),
                (errors) => {
                  console.error("[Form] 유효성 검증 실패:", errors);
                  const messages = Object.values(errors)
                    .map((e: any) => e?.message)
                    .filter(Boolean);
                  ToastAlert(messages[0] || "필수 항목을 확인해주세요.");
                }
              )();
            }}
          />

          <BasicButton
            title="샤카라카에 상품 등록"
            padding="1.6rem"
            fontSize="1.6rem"
            height="5.2rem"
            width="100%"
            disabled={llmLoading}
            onClick={() => {
              handleSubmit(
                (data) => onSubmit(data, "product"),
                (errors) => {
                  console.error("[Form] 유효성 검증 실패:", errors);
                  const messages = Object.values(errors)
                    .map((e: any) => e?.message)
                    .filter(Boolean);
                  ToastAlert(messages[0] || "필수 항목을 확인해주세요.");
                }
              )();
            }}
          />
        </ButtonWrapper>

        <BasicModal
          open={modals.backModal}
          closeModal={() => closeModal("backModal")}
          activeEvent={() => {
            closeModal("backModal");
            navigate("/capture-list");
          }}
          activeName="확인"
          closeName="취소">
          <ModalContent mainMessage="이 상품을 저장하지 않으시겠습니까?" />
        </BasicModal>

        <BasicModal
          open={modals.policyModal}
          closeModal={() => closeModal("policyModal")}
          activeEvent={() => {
            closeModal("policyModal");
            navigate("/sales-policy");
          }}
          activeName="판매자 정책 설정하기"
          closeName="취소">
          <Flex vertical gap="2rem" align="center">
            <Image
              src={theme.image.warning_circle_thin.default}
              size="4.8rem"
            />

            <ModalContent
              mainMessage="판매자 정책 설정이 안되어 있어요!"
              subMessage="설정을 완료하신 후 등록을 진행해주세요"
            />
          </Flex>
        </BasicModal>

        <BasicModal
          open={modals.bankAccountModal}
          closeModal={() => closeModal("bankAccountModal")}
          activeName="계좌 등록하기"
          activeEvent={() => {
            closeModal("bankAccountModal");
            navigate("/bank-account-management");
          }}
          closeName="취소">
          <Flex vertical gap="2rem" align="center">
            <Image
              src={theme.image.warning_circle_thin.default}
              size="4.8rem"
            />

            <ModalContent
              mainMessage="계좌 등록이 안되어 있어요!"
              subMessage="계좌등록을 완료하신 후 등록을 진행해주세요"
            />
          </Flex>
        </BasicModal>
      </Container>
    </FormProvider>
  );
};

export default CaptureDetail;

const Container = styled.form(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
  padding: "2rem",
  zIndex: 1,
  position: "relative",
}));

const StyledDate = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  color: theme.color.gray,
  lineHeight: "1.8rem",
}));

const NoticeBox = styled.div(({ theme }) => ({
  backgroundColor: theme.color.light_green,
  display: "flex",
  padding: "1rem",
  gap: "0.5rem",
  lineHeight: "18px",
}));

const StrongText = styled.div(({ theme }) => ({
  color: theme.color.green,
  fontWeight: theme.fontWeight.bold,
  fontSize: "1.2rem",
}));

const NormalText = styled.div(({ theme }) => ({
  color: theme.color.gray,

  fontSize: "1.2rem",
}));

const ButtonWrapper = styled.div(() => ({
  display: "flex",
  gap: "1rem",
}));

const ErrorMessage = styled.div(({ theme }) => ({
  color: theme.color.orange,
  fontSize: "1.2rem",
}));
